import React, { useCallback, useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { useApiCaller } from '../../utils/ApiUtilities';
import { useError } from '../ErrorContext';
import {
    getAdminRewardsUrl,
    getRewardGrantUrl,
    getRewardGrantBulkUrl,
    getUserSearchUrl,
} from '../../utils/UrlUtilities';
import { constructRewardGrantPayload, constructRewardGrantBulkPayload } from '../../utils/PayloadUtils';
import { REWARD_KIND_LABELS, REWARD_PERIOD_OPTIONS, itemsPerPageOptions } from '../../utils/Constants';
import { isEmpty } from '../../utils/BasicUtilities';

const KIND_BADGE = {
    CORRECT_200: 'bg-primary',
    STREAK_7: 'bg-info text-dark',
    MONTHLY_TOP1PCT: 'bg-success',
    ADMIN_GRANT: 'bg-warning text-dark',
};

export default function RewardsManagement() {
    const CallApi = useApiCaller();
    const { showSuccess } = useError();

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalStars, setTotalStars] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [loading, setLoading] = useState(false);
    const [pending, setPending] = useState({ q: '', period: '' });
    const [applied, setApplied] = useState({ q: '', period: '' });

    // Grant modal — mode 'user' (single) or 'bulk' (all filtered).
    const [grantMode, setGrantMode] = useState(null);
    const [grantForm, setGrantForm] = useState({ stars: 10, reason: '' });
    const [granting, setGranting] = useState(false);
    // User picker (single-grant).
    const [userQuery, setUserQuery] = useState('');
    const [userResults, setUserResults] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);

    const fetchRows = useCallback(async () => {
        setLoading(true);
        const params = { page, limit };
        if (!isEmpty(applied.q)) params.q = applied.q;
        if (!isEmpty(applied.period)) params.period = applied.period;
        const res = await CallApi({ url: getAdminRewardsUrl(params), method: 'GET', token: true });
        if (res.success) {
            setRows(res.data.rewards || []);
            setTotal(res.data.total || 0);
            setTotalStars(res.data.total_stars_in_filter || 0);
        }
        setLoading(false);
    }, [page, limit, applied, CallApi]);

    useEffect(() => { fetchRows(); }, [fetchRows]);

    // Debounced user search for single-grant.
    useEffect(() => {
        if (isEmpty(userQuery) || selectedUser) { setUserResults([]); return undefined; }
        let cancelled = false;
        const t = setTimeout(async () => {
            const res = await CallApi({ url: getUserSearchUrl({ q: userQuery, limit: 8 }), method: 'GET', token: true });
            if (!cancelled && res.success) setUserResults(res.data || []);
        }, 300);
        return () => { cancelled = true; clearTimeout(t); };
    }, [userQuery, selectedUser, CallApi]);

    const openUserGrant = (row = null) => {
        setGrantMode('user');
        setGrantForm({ stars: 10, reason: '' });
        if (row) {
            setSelectedUser({ id: row.user_id, name: row.user_name, email: row.user_email });
            setUserQuery(`${row.user_name} (${row.user_email})`);
        } else {
            setSelectedUser(null);
            setUserQuery('');
        }
        setUserResults([]);
    };

    const openBulkGrant = () => {
        setGrantMode('bulk');
        setGrantForm({ stars: 10, reason: '' });
    };

    const closeGrant = () => {
        setGrantMode(null);
        setSelectedUser(null);
        setUserQuery('');
        setUserResults([]);
    };

    const handleGrant = async () => {
        if (!(Number(grantForm.stars) > 0)) return;
        setGranting(true);
        let res;
        if (grantMode === 'user') {
            if (!selectedUser) { setGranting(false); return; }
            res = await CallApi({
                url: getRewardGrantUrl(),
                method: 'POST',
                payload: constructRewardGrantPayload({ userId: selectedUser.id, stars: grantForm.stars, reason: grantForm.reason }),
                token: true,
            });
        } else {
            res = await CallApi({
                url: getRewardGrantBulkUrl(),
                method: 'POST',
                payload: constructRewardGrantBulkPayload({ q: applied.q, period: applied.period, stars: grantForm.stars, reason: grantForm.reason }),
                token: true,
            });
        }
        setGranting(false);
        if (res.success) {
            showSuccess(res.data?.detail || 'Stars granted.');
            closeGrant();
            fetchRows();
        }
    };

    const totalPages = Math.ceil(total / limit) || 1;
    const periodLabel = REWARD_PERIOD_OPTIONS.find((o) => o.value === applied.period)?.label || 'All time';

    return (
        <div className="container py-4">
            <h2 className="fw-bold mb-3" style={{ color: '#4c1d95' }}>
                <Star size={24} className="me-2" fill="#facc15" color="#facc15" />Rewards Management
            </h2>

            {/* Filters + actions */}
            <div className="card mb-3 kp-filter"><div className="card-body row g-2 align-items-end">
                <div className="col-12 col-md-4">
                    <label className="form-label small mb-1">Search user</label>
                    <input className="form-control form-control-sm" value={pending.q} onChange={(e) => setPending({ ...pending, q: e.target.value })} placeholder="name / email" />
                </div>
                <div className="col-6 col-md-3">
                    <label className="form-label small mb-1">Period</label>
                    <select className="form-select form-select-sm" value={pending.period} onChange={(e) => setPending({ ...pending, period: e.target.value })}>
                        {REWARD_PERIOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
                <div className="col-6 col-md-2">
                    <button className="btn btn-sm btn-outline-primary w-100" onClick={() => { setApplied(pending); setPage(1); }}>Filter</button>
                </div>
                <div className="col-6 col-md-3 d-flex gap-2">
                    <button className="btn btn-sm btn-primary flex-fill" onClick={() => openUserGrant()}>Grant a user</button>
                    <button className="btn btn-sm btn-outline-warning flex-fill" onClick={openBulkGrant} disabled={total === 0}>Grant filtered</button>
                </div>
            </div></div>

            <div className="mb-2 small text-muted">
                Showing <strong>{periodLabel}</strong> · {total} reward{total === 1 ? '' : 's'} · <span className="text-warning fw-bold">★ {totalStars}</span> total stars in filter
            </div>

            {/* Grid */}
            <div className="card"><div className="card-body p-0 table-responsive">
                <table className="table table-hover mb-0 align-middle">
                    <thead className="table-light">
                        <tr><th>User</th><th>Reward</th><th>Stars</th><th>Details</th><th>By</th><th>Date</th><th className="text-end">Actions</th></tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} className="text-center text-muted py-4">Loading…</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={7} className="text-center text-muted py-4">No rewards found.</td></tr>
                        ) : rows.map((r) => (
                            <tr key={r.id}>
                                <td>
                                    <div className="fw-semibold">{r.user_name || '—'}</div>
                                    <div className="small text-muted">{r.user_email}</div>
                                </td>
                                <td><span className={`badge ${KIND_BADGE[r.kind] || 'bg-secondary'}`}>{REWARD_KIND_LABELS[r.kind] || r.kind}</span></td>
                                <td><span className="text-warning fw-bold">★ {r.stars}</span></td>
                                <td className="small text-muted">{r.level ? `${r.level} ` : ''}{r.period ? `· ${r.period} ` : ''}{r.reason || ''}</td>
                                <td className="small text-muted">{r.granted_by || '—'}</td>
                                <td className="small text-muted">{new Date(r.created_at).toLocaleDateString()}</td>
                                <td className="text-end">
                                    <button className="btn btn-sm btn-outline-primary" onClick={() => openUserGrant(r)}>Grant stars</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div></div>

            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="d-flex align-items-center gap-2">
                    <label className="small mb-0">Items per page:</label>
                    <select className="form-select form-select-sm w-auto" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}>
                        {itemsPerPageOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
                    <span className="small text-muted">Page {page} of {totalPages}</span>
                    <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
                </div>
            </div>

            {/* Grant modal */}
            {grantMode && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1060 }}>
                    <div className="card" style={{ width: '90%', maxWidth: 'min(480px, 94vw)' }}>
                        <div className="card-header fw-bold" style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff' }}>
                            {grantMode === 'bulk' ? 'Grant stars to all filtered users' : 'Grant stars to a user'}
                        </div>
                        <div className="card-body">
                            {grantMode === 'user' ? (
                                <div className="mb-2" style={{ position: 'relative' }}>
                                    <label className="form-label small mb-1">Recipient (name / email / phone)</label>
                                    <input
                                        className="form-control form-control-sm"
                                        placeholder="Search a user…"
                                        value={userQuery}
                                        onChange={(e) => { setUserQuery(e.target.value); setSelectedUser(null); }}
                                    />
                                    {userResults.length > 0 && (
                                        <ul className="list-group position-absolute w-100" style={{ zIndex: 20, maxHeight: 220, overflowY: 'auto' }}>
                                            {userResults.map((u) => (
                                                <li key={u.id} className="list-group-item list-group-item-action" role="button" onClick={() => { setSelectedUser(u); setUserQuery(`${u.name} (${u.email})`); setUserResults([]); }}>
                                                    <div className="fw-semibold small">{u.name}</div>
                                                    <div className="small text-muted">{u.email}</div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ) : (
                                <p className="small text-muted mb-2">
                                    Grants to every distinct user in the current filter (<strong>{periodLabel}</strong>{applied.q ? `, "${applied.q}"` : ''}) — {total} reward row(s).
                                </p>
                            )}
                            <div className="row g-2">
                                <div className="col-4">
                                    <label className="form-label small mb-1">Stars</label>
                                    <input type="number" min="1" className="form-control form-control-sm" value={grantForm.stars} onChange={(e) => setGrantForm({ ...grantForm, stars: e.target.value })} />
                                </div>
                                <div className="col-8">
                                    <label className="form-label small mb-1">Reason</label>
                                    <input className="form-control form-control-sm" value={grantForm.reason} onChange={(e) => setGrantForm({ ...grantForm, reason: e.target.value })} placeholder="e.g. Contest winner" />
                                </div>
                            </div>
                        </div>
                        <div className="card-footer d-flex justify-content-end gap-2">
                            <button className="btn btn-outline-secondary" onClick={closeGrant} disabled={granting}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleGrant} disabled={granting || !(Number(grantForm.stars) > 0) || (grantMode === 'user' && !selectedUser)}>
                                {granting ? 'Granting…' : 'Grant stars'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
